import { useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { PageHeader, Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Input, Select } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Avatar } from '../../components/ui/Avatar'
import { usersApi } from '../../api/users.api'
import { authApi } from '../../api/auth.api'
import { useAuth } from '../../auth/AuthContext'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import { useThemeStore, THEME_LABELS, type ThemeName } from '../../store/themeStore'
import type { UserStatus } from '../../api/types'

const STATUS_OPTIONS: { value: UserStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Aktiv' },
  { value: 'AWAY', label: 'Abwesend' },
  { value: 'DO_NOT_DISTURB', label: 'Nicht stören' },
  { value: 'OFFLINE', label: 'Offline erscheinen' },
]

const THEME_OPTIONS = Object.keys(THEME_LABELS) as ThemeName[]

const ALLOWED_AVATAR_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif'])
const MAX_AVATAR_SIZE_BYTES = 5 * 1024 * 1024

export function ProfilePage() {
  const { me, refresh } = useAuth()
  const { theme, setTheme } = useThemeStore()

  const [displayName, setDisplayName] = useState(me?.user.displayName ?? '')
  const [email, setEmail] = useState(me?.user.email ?? '')
  const [avatarUrl, setAvatarUrl] = useState(me?.user.avatarUrl ?? '')
  const [status, setStatus] = useState<UserStatus>(me?.user.status ?? 'ACTIVE')

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const avatarInputRef = useRef<HTMLInputElement>(null)
  const avatarUploadMutation = useMutation({
    mutationFn: (file: File) => usersApi.uploadAvatar(file),
    onSuccess: async (res) => {
      setAvatarUrl(res.data.avatarUrl ?? '')
      await refresh()
      toast.success('Profilbild aktualisiert.')
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Upload fehlgeschlagen.'),
  })

  const onAvatarFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!ALLOWED_AVATAR_TYPES.has(file.type)) {
      toast.error('Nur PNG, JPEG, WEBP oder GIF sind als Profilbild erlaubt.')
      return
    }
    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      toast.error('Datei ist zu groß (max. 5 MB).')
      return
    }
    avatarUploadMutation.mutate(file)
  }

  const profileMutation = useMutation({
    mutationFn: () => usersApi.updateOwnProfile({ displayName, email, avatarUrl, status }),
    onSuccess: async () => {
      await refresh()
      toast.success('Profil aktualisiert.')
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const passwordMutation = useMutation({
    mutationFn: () => authApi.changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      toast.success('Passwort geändert.')
      setCurrentPassword('')
      setNewPassword('')
    },
    onError: (err) => setPasswordError(err instanceof ApiError ? err.message : 'Ändern fehlgeschlagen.'),
  })

  const onSubmitProfile = (e: FormEvent) => {
    e.preventDefault()
    profileMutation.mutate()
  }

  const onSubmitPassword = (e: FormEvent) => {
    e.preventDefault()
    setPasswordError(null)
    passwordMutation.mutate()
  }

  if (!me) return null

  return (
    <div>
      <PageHeader title="Mein Profil" subtitle={`@${me.user.username} · ${me.roles.join(', ') || 'Keine Rollen'}`} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--space-4)' }}>
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
            <Avatar name={displayName || me.user.displayName} imageUrl={avatarUrl} size="lg" status={status} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{me.user.displayName}</div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={onAvatarFileChange}
                style={{ display: 'none' }}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                loading={avatarUploadMutation.isPending}
                onClick={() => avatarInputRef.current?.click()}
                style={{ marginTop: 'var(--space-1)' }}
              >
                Bild hochladen
              </Button>
            </div>
          </div>
          <form onSubmit={onSubmitProfile} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <Field label="Anzeigename" htmlFor="p-displayName">
              <Input id="p-displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
            </Field>
            <Field label="E-Mail" htmlFor="p-email">
              <Input id="p-email" type="email" value={email ?? ''} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Profilbild-URL (optional)" htmlFor="p-avatar">
              <Input id="p-avatar" value={avatarUrl ?? ''} onChange={(e) => setAvatarUrl(e.target.value)} />
            </Field>
            <Field label="Status" htmlFor="p-status">
              <Select id="p-status" value={status} onChange={(e) => setStatus(e.target.value as UserStatus)}>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Button type="submit" variant="primary" loading={profileMutation.isPending} style={{ alignSelf: 'flex-start' }}>
              Speichern
            </Button>
          </form>
        </Card>

        <Card>
          <h3 style={{ marginTop: 0 }}>Theme</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', marginTop: 0 }}>
            Nur auf diesem Gerät gespeichert, gilt für dich persönlich.
          </p>
          <Field label="Farbschema" htmlFor="p-theme">
            <Select id="p-theme" value={theme} onChange={(e) => setTheme(e.target.value as ThemeName)}>
              {THEME_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {THEME_LABELS[t]}
                </option>
              ))}
            </Select>
          </Field>
        </Card>

        <Card>
          <h3 style={{ marginTop: 0 }}>Passwort ändern</h3>
          <form onSubmit={onSubmitPassword} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {passwordError ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{passwordError}</div> : null}
            <Field label="Aktuelles Passwort" htmlFor="p-current">
              <Input
                id="p-current"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </Field>
            <Field label="Neues Passwort" htmlFor="p-new" hint="Mindestens 10 Zeichen">
              <Input
                id="p-new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={10}
              />
            </Field>
            <Button type="submit" variant="primary" loading={passwordMutation.isPending} style={{ alignSelf: 'flex-start' }}>
              Passwort ändern
            </Button>
          </form>
        </Card>
      </div>
    </div>
  )
}
